import { Box, Typography } from '@mui/material';
import { dashboardMobileWidthValue } from '@/constants';
import { useLocalization } from '@/hooks/useLocalization';
import { useToday } from '@/hooks/useToday';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';
import type { StandardWidgetProps } from '@/models/WidgetModels';
import AssetAllocationPieChart from '@/modules/portfolio/charts/assetAllocationPieChart';
import { toUtcCalendarDate } from '@/utils/date';
import { ContentContainer } from '@/widgets/contentContainer';

/**
 * No exclusions. The pie falls back to the filters saved from a client's
 * Asset Allocation page when given none, and those would drop that client's
 * accounts from the whole book.
 */
const NO_EXCLUSIONS: number[] = [];

/**
 * The advisor's whole book as one Current Allocation pie, as of today (legacy
 * `advisor-diversification`, IU-327). The pie is asked for no client, so the
 * advisor endpoint aggregates every client the advisor can see, following the
 * firm's primary-clients setting and counting held-away accounts. Like legacy
 * it has no date picker; today moves on at midnight, so a dashboard left
 * open overnight does not keep yesterday's date. It leaves out the Target
 * pie, which belongs to a single client, and ignores the filters saved from
 * a client's Asset Allocation page. Slices still drill into
 * sub-classifications and holdings, which the advisor endpoint scopes to the
 * book, but the holding detail flyout stays closed because its queries need a
 * client. A failed load offers Retry, as the other advisor widgets do. Half
 * width, or a tile narrower than the mobile width, uses the mobile layout.
 */
export default function DashboardPortfolioDiversificationWidget({
  title,
  onDataLoaded,
  isWidget = true,
  isHalfWidth = false,
  container = 'widget',
}: StandardWidgetProps) {
  const { t } = useLocalization();
  const widgetTitle =
    title || t('widgetPortfolioDiversification', 'Portfolio Diversification');
  const today = useToday();
  const { ref, isBelow } = useWidgetWidthIsBelow(
    isWidget,
    dashboardMobileWidthValue,
  );
  const forceMobile = isWidget && (isHalfWidth || isBelow);

  return (
    <ContentContainer
      container={container}
      ref={ref}
    >
      <Box sx={{ mb: 2 }}>
        <Typography variant='h5'>{widgetTitle}</Typography>
      </Box>
      <AssetAllocationPieChart
        asOfDate={toUtcCalendarDate(today)}
        excludedAccounts={NO_EXCLUSIONS}
        excludedCategories={NO_EXCLUSIONS}
        excludedAccountTypes={NO_EXCLUSIONS}
        excludedInstitutions={NO_EXCLUSIONS}
        onDataLoaded={onDataLoaded}
        forceMobile={forceMobile}
        isWidget={isWidget}
        hideTarget
        showRetry
        showNoDataMessage
        disableHoldingDetails
      />
    </ContentContainer>
  );
}
